import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAssistant } from '../AssistantDock';

/** A line icon (lucide geometry), same set and weight as the campaign portal. */
const Icon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}
    strokeLinecap="round" strokeLinejoin="round" className="w-[18px] h-[18px] flex-none">
    {children}
  </svg>
);

const ICON = {
  assets: <Icon><rect width="18" height="18" x="3" y="3" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></Icon>,
  creative: <Icon><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M7 3v18" /><path d="M3 7.5h4" /><path d="M3 12h18" /><path d="M3 16.5h4" /><path d="M17 3v18" /><path d="M17 7.5h4" /><path d="M17 16.5h4" /></Icon>,
  album: <Icon><path d="M18 22H4a2 2 0 0 1-2-2V6" /><path d="m22 13-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18" /><circle cx="12" cy="8" r="2" /><rect width="16" height="16" x="6" y="2" rx="2" /></Icon>,
  order: <Icon><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="M11 4h10" /><path d="M11 8h7" /><path d="M11 12h4" /></Icon>,
  templates: <Icon><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M8 13h8" /><path d="M8 17h5" /></Icon>,
  cadences: <Icon><path d="m17 2 4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" /><path d="m7 22-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" /></Icon>,
  knowledge: <Icon><path d="M12 7v14" /><path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" /></Icon>,
  collapse: <Icon><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" /><path d="m16 15-3-3 3-3" /></Icon>,
  expand: <Icon><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M9 3v18" /><path d="m14 9 3 3-3 3" /></Icon>,
  assistant: <Icon><path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12Z" /><path d="M9 12h.01M12 12h.01M15 12h.01" /></Icon>,
  signOut: <Icon><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" x2="9" y1="12" y2="12" /></Icon>,
};

type NavItem = { to: string; label: string; icon: React.ReactNode };

const NAV: { group: string; items: NavItem[] }[] = [
  { group: 'Media', items: [
    { to: '/assets', label: 'Assets', icon: ICON.assets },
    { to: '/creative-library', label: 'Creative Library', icon: ICON.creative },
  ] },
  { group: 'Album', items: [
    { to: '/album', label: 'Project Album', icon: ICON.album },
    { to: '/album/order', label: 'Album Order', icon: ICON.order },
  ] },
  { group: 'Messages', items: [
    { to: '/whatsapp-templates', label: 'Templates', icon: ICON.templates },
    { to: '/cadences', label: 'Cadences', icon: ICON.cadences },
  ] },
  { group: 'Agent', items: [
    { to: '/knowledge-base', label: 'Knowledge Base', icon: ICON.knowledge },
  ] },
];

/** Collapsed = icons only; remembered per browser (a viewer convenience). */
const KEY = 'sidebar-collapsed';
const readCollapsed = () => { try { return localStorage.getItem(KEY) === '1'; } catch { return false; } };

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const assistant = useAssistant();
  const toggle = () => setCollapsed((c) => {
    try { localStorage.setItem(KEY, c ? '0' : '1'); } catch { /* private window: just don't remember */ }
    return !c;
  });

  return (
    <aside className={`${collapsed ? 'w-[68px]' : 'w-60'} flex-none h-full flex flex-col bg-cream border-r border-charcoal/10 transition-[width] duration-200`}>
      <div className={`flex items-center pt-5 pb-4 ${collapsed ? 'flex-col gap-3 px-2' : 'gap-3 pl-5 pr-3'}`}>
        <Link to="/assets" className="flex items-center gap-3 min-w-0" title="Marketing Dashboard">
          <img src="/logo.png" alt="Bhuvana Fortune Greens" className={`${collapsed ? 'h-7' : 'h-9'} w-auto`} />
          {!collapsed && <span className="font-display text-[15px] text-forest font-semibold tracking-tight leading-tight">Marketing<br />Dashboard</span>}
        </Link>
        <button onClick={toggle} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                className={`w-8 h-8 grid place-items-center rounded-lg text-graphite hover:text-charcoal hover:bg-charcoal/5 flex-none ${collapsed ? '' : 'ml-auto'}`}>
          {collapsed ? ICON.expand : ICON.collapse}
        </button>
      </div>

      <nav className={`flex-1 pb-3 overflow-y-auto overflow-x-hidden ${collapsed ? 'px-2.5' : 'px-3'}`}>
        {NAV.map(({ group, items }, gi) => (
          <div key={group} className="space-y-0.5">
            {collapsed
              ? gi > 0 && <div className="mx-2 my-3 border-t border-charcoal/10" />
              : <div className={`text-[10.5px] uppercase tracking-[0.1em] font-semibold text-graphite/70 px-3 pb-1.5 ${gi > 0 ? 'pt-5' : 'pt-1'}`}>{group}</div>}
            {items.map((item) => {
              const active = pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  title={collapsed ? item.label : undefined}
                  aria-label={collapsed ? item.label : undefined}
                  className={`relative flex items-center gap-3 h-9 rounded-lg ${collapsed ? 'justify-center' : 'px-3'} text-[13.5px] transition-colors ${
                    active
                      ? 'bg-warm-white text-forest font-semibold shadow-[0_1px_2px_rgba(61,61,61,0.08)] ring-1 ring-charcoal/[0.06]'
                      : 'text-charcoal/80 font-medium hover:bg-charcoal/[0.04] hover:text-charcoal'
                  }`}
                >
                  {active && <span className={`absolute ${collapsed ? '-left-2.5' : '-left-3'} top-2 bottom-2 w-[3px] rounded-r bg-forest`} />}
                  <span className={active ? '' : 'opacity-85'}>{item.icon}</span>
                  {!collapsed && item.label}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {assistant.available && (
        <div className={`pb-2 ${collapsed ? 'px-2.5' : 'px-3'}`}>
          <button
            onClick={assistant.toggle}
            aria-pressed={assistant.mode !== 'closed'}
            title={collapsed ? 'Assistant' : undefined}
            className={`relative w-full flex items-center gap-3 h-9 rounded-lg ${collapsed ? 'justify-center' : 'px-3'} text-[13.5px] transition-colors ${
              assistant.mode !== 'closed'
                ? 'bg-warm-white text-forest font-semibold shadow-[0_1px_2px_rgba(61,61,61,0.08)] ring-1 ring-charcoal/[0.06]'
                : 'text-forest font-semibold hover:bg-charcoal/[0.04]'
            }`}
          >
            {ICON.assistant}
            {!collapsed && 'Assistant'}
            <span
              title={assistant.online === null ? 'Connecting' : assistant.online ? (assistant.busy ? 'Engine busy' : 'Engine online') : 'Engine offline'}
              className={`${collapsed ? 'absolute top-1.5 right-1.5' : 'ml-auto'} w-2 h-2 rounded-full ${
                assistant.online === null ? 'bg-charcoal/20' : assistant.online ? (assistant.busy ? 'bg-gold' : 'bg-forest') : 'bg-brick'
              }`}
            />
          </button>
        </div>
      )}
      {user && (
        <div className={`py-3 border-t border-charcoal/10 flex items-center gap-2.5 ${collapsed ? 'flex-col px-2' : 'px-3'}`}
             title={collapsed ? user.email : undefined}>
          {user.picture
            ? <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full flex-none" />
            : <span className="w-8 h-8 rounded-full bg-forest text-warm-white grid place-items-center font-semibold text-[13px] flex-none">
                {(user.name || user.email).charAt(0).toUpperCase()}
              </span>}
          {!collapsed && (
            <span className="min-w-0 flex-1">
              <span className="block text-[12.5px] font-semibold truncate">{user.name || user.email}</span>
              {user.name && <span className="block text-[11px] text-graphite truncate">{user.email}</span>}
            </span>
          )}
          <button
            onClick={() => { logout(); navigate('/login'); }}
            title="Sign out"
            aria-label="Sign out"
            className="w-8 h-8 grid place-items-center rounded-lg text-graphite hover:text-brick hover:bg-brick/5 flex-none"
          >
            {ICON.signOut}
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
